/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import isUndefined from 'lodash.isundefined';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadAssets as getAssets} from '@ion/core/src/services/Connect';
import {
  Asset,
  AssetsFilters,
  AssetType
} from '@ion/core/src/types/entities';
import {AssetSearchableProperty} from '@ion/core/src/types/services';
import {useAssetsDropdown} from '@ion/query/src/hooks/useAssetsDropdown';
import Dropdown, {
  DropDownDefaultProps,
  DropDownInfiniteScrollProps,
  Options
} from '../components/menus/Dropdown';
import {dlsColor, dlsTypography} from '../theme/theme';
import {BodyTypography} from '../typography/BodyTypography';
import {AdminGroupLevelProps} from './AdminGroupEditor';
import {AssetAssociation} from './AssetEditor';
import GroupChips from './GroupChips';
import {getOptions, getValues} from './ListEditorUtils';

// defines the asset list prop definitions ...
export type AssetListProps = & DropDownDefaultProps &
AdminGroupLevelProps & AssetAssociation & DropDownInfiniteScrollProps & {
  accountId: number;
  onChange?: Function;
  readOnly?: boolean;
  required?: boolean;
  validationError?: string;
  classes?: string;
  defaultValue?: any;
  hasDetailedViewLink?: boolean;
  detailedViewLinkText?: string;
  onDetailedLinkClick?: Function;
  loading?: boolean;
  hasError?: boolean;
  setAssetDetailedRow?: Function;
  isEditMode?: boolean;
  viewGroup?: boolean;
  assetTypeValue?: AssetsFilters['assetType'];
}

// default styles used by the component when a classes prop is not provided ...
const useStyles = makeStyles((theme) => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  },
  boxStyle : {
    marginTop : '14px',
    fontSize  : 'xx-large'
  },
  boxfieldLabel : {
    color        : dlsColor('grey2'),
    marginBottom : '8px',
    ...dlsTypography('body3')
  }
}));

const MIN_INPUT_LENGTH = 0;
const MAX_INPUT_LENGTH = 3;

/**
 * @AssetList
 *
 * The AssetList provides a list of assets for the selected account or
 * user's default account for display within a dropdown list and
 * allows the user to select an Asset from the list which will be returned to the
 * client as the selected Asset.
 */
export const AssetList = ({
  accountId,
  onChange,
  readOnly,
  required,
  classes,
  defaultValue,
  hasDetailedViewLink,
  onDetailedLinkClick,
  loading,
  setAssetDetailedRow,
  isEditMode,
  viewGroup,
  onChipClick,
  assetId,
  detailedViewLinkText = null,
  hasError = false,
  isChipClickable = false,
  viewChip = false,
  includeFilteredAssetAssociation = false,
  isInfiniteScroll = false,
  assetTypeValue = null
}: AssetListProps) => {
  const _classes                            = useStyles();
  const [isLoading, setIsLoading]           = useState<boolean>(loading);
  const [assets, setAssets]                 = useState<Asset[]>([]);
  const [selected, setSelected]             = React.useState<string[]>([]);
  const [options, setOptions]               = useState<Options[]>([]);
  const [savedAssets, setSavedAssets]       = useState<Asset[]>([]);
  const [allAssets, setAllAssets]           = useState<Asset[]>([]);
  const hasMounted                          = useHasMounted();
  const [pageId, setPageId]                 = useState<number>(1);
  const [isScroll, setIsScroll]             = useState<boolean>(false);
  const [isSearch, setIsSearch]             = useState<boolean>(false);
  const [searchTerms, setSearchTerms]       = useState<string>();
  const [isCleared, setIsCleared]           = useState<boolean>(false);
  const [isOutsideClick, setIsOutsideClick] = useState<boolean>(false);
  const [scrolledAssets, setScrolledAssets] = useState([]);
  const {loadAsset}                         = useAssetsDropdown();

  // whenever the account changes the Assets are reloaded ...
  useEffect(() => {
    accountId && fetchAssets(accountId);
  }, [accountId, assetTypeValue]);

  useEffect(() => {
    if (defaultValue) {
      setSavedAssets(defaultValue);
      setSelected(getValues(defaultValue));
    }
  }, [defaultValue]);

  // if a default Asset item is provided set it as the selected ...
  useEffect(() => {
    if (defaultValue && !isInfiniteScroll) {
      setSelected(getValues(defaultValue));
    }
  }, [options]);

  useEffect(() => {
    if ((isInfiniteScroll && isScroll) ||
    (isInfiniteScroll &&
      (searchTerms?.length > MAX_INPUT_LENGTH || searchTerms?.length === MIN_INPUT_LENGTH)
    )) {
      handleAssestList();
    }
  }, [pageId, searchTerms, isScroll, isSearch, isCleared, assets, isInfiniteScroll, accountId]);

  useEffect(() => {
    if (isInfiniteScroll && isScroll) {
      setOptions(getOptions(scrolledAssets));
      setIsOutsideClick(false);
    }
  }, [isInfiniteScroll, scrolledAssets]);

  useEffect(() => {
    if (isInfiniteScroll && isOutsideClick) {
      setOptions(getOptions(assets));
    }
  }, [isInfiniteScroll, assets, isOutsideClick]);

  //Fetch Assets
  const fetchAssets = async function(searchTerm: any, filter = {}) {
    let assets: Asset[];
    setIsLoading(true);
    try {
      const results = await getAssets({
        pageId   : 1,
        pageSize : 100,
        sortOn   : ['+name'],
        filterOn : getFilter(assetTypeValue, filter),
        accountId
      });
      assets = results?.assets;
    } catch (err) {
      setOptions([]);
      error(`Asset: Error occurred retrieving Assets for account ${accountId}`);
    } finally {
      if (hasMounted !== false) {
        setAssets(assets);
        if (isEditMode && includeFilteredAssetAssociation) {
          assets = assets?.filter(({id}) => id !== assetId);
        }
        setOptions(getOptions(assets));
        setIsLoading(false);
        if (isUndefined(searchTerms) && isInfiniteScroll) {
          setScrolledAssets(scrolledAssets?.concat(assets));
        }
      }
    }
  };

  const getFilter = (filterValue, filter) => {
    if (filterValue === AssetType.Vehicle.toUpperCase()) {
      return {[AssetSearchableProperty.assetType]: '-VEHICLE'};
    }
    if (filterValue === AssetType.Tool.toUpperCase()) {
      return {[AssetSearchableProperty.assetType]: '-TOOL'};
    }
    return filter;
  };

  useEffect(() => {
    setAllAssets([
      ...(assets ?? []),
      ...(savedAssets ?? []),
      ...(scrolledAssets ?? [])
    ]);
  }, [assets, savedAssets, scrolledAssets]);

  const handleAssestList = useCallback(() => {
    let assets: Asset[];
    try {
      loadAsset({
        pageSize    : 100,
        pageId      : pageId,
        searchTerms : searchTerms,
        accountId
      }, {
        onSuccess : (asset) => {
          assets = asset?.assets;
          setScrolledAssets(scrolledAssets?.concat(assets));
          if (isEditMode && isSearch) {
            assets = assets?.filter(({id}) => id !== assetId);
          } if (isSearch || (searchTerms?.length === MIN_INPUT_LENGTH && isCleared) || !isEmpty(setSelected)) {
            setOptions(getOptions(assets));
            setIsOutsideClick(false);
          }
        },
        onError : () => {
          setOptions([]);
        }
      });
    } catch (err) {
      setOptions([]);
    }
  }, [pageId, searchTerms, isScroll, isSearch, isCleared, accountId, isInfiniteScroll]);

  const handleChange = useCallback(function(event: React.ChangeEvent, child) {
    const idValues = child?.map(({id}) => id);
    const assetsList = allAssets?.filter((item) => {
      return idValues.includes(item.id);
    });
    setAssetDetailedRow(child);
    setSelected(child);
    onChange?.(assetsList);
    if (isInfiniteScroll) {
      handleAssetClickOutside();
    }
  }, [setAssetDetailedRow, allAssets]);

  const onInfiniteScroll = useCallback((scrollHeight, scrollTop, clientHeight) => {
    if (scrollTop + clientHeight >= scrollHeight - 2) {
      setPageId((prevActiveStep) => prevActiveStep + 1);
      setIsScroll(true);
      setIsSearch(false);
      setIsCleared(false);
    }
  }, []);

  const onSearchAssets = useCallback((value: string) => {
    if (value?.length > MAX_INPUT_LENGTH) {
      setSearchTerms(value);
      setIsSearch(true);
      setPageId(1);
    } else if (value?.length === MIN_INPUT_LENGTH) {
      setSearchTerms('');
      setIsSearch(false);
      setIsCleared(true);
      setScrolledAssets([]);
      setPageId(1);
    }
  }, []);

  const handleAssetClickOutside = useCallback(() => {
    setIsOutsideClick(true);
    setPageId(1);
    setSearchTerms('');
    setIsSearch(false);
    setIsCleared(true);
    setScrolledAssets([]);
  }, []);

  return (
    <>
      <div className={classes || _classes.root}>
        {(!viewChip || isEmpty(selected)) && (
          <Dropdown required={required}
            hasError={hasError}
            disabled={readOnly || isLoading || viewGroup}
            onChipClick={onChipClick}
            isChipClickable={isChipClickable}
            onInfiniteScroll={onInfiniteScroll}
            isInfiniteScroll={isInfiniteScroll}
            onSearchAssociations={onSearchAssets}
            onHandleClickOutside={handleAssetClickOutside}
            options={options}
            multiSelect
            loading={isLoading}
            ctaLinkText={detailedViewLinkText}
            hasCTALink={hasDetailedViewLink}
            hasClearAll={isEditMode && !viewGroup}
            onCTALinkClick={onDetailedLinkClick}
            value={isLoading ? translate('common:states.loading') : selected}
            fieldLabel={translate('common:editors.asset.label')}
            placeholder={translate('common:editors.asset.placeholder')}
            preferPlaceholder={selected.length === 0 && !isLoading}
            onChange={handleChange} />
        )}
      </div>
      <div className={_classes.boxStyle}>
        {!isEmpty(selected) && viewChip && (
          <>
            <BodyTypography className={_classes.boxfieldLabel}>
              {translate('common:editors.asset.label')}
            </BodyTypography>
            <GroupChips value={selected}
              onChipClick={onChipClick}
              chips={savedAssets} />
          </>
        )}
      </div>
    </>
  );
};

export default AssetList;
